import { zodResolver } from "@hookform/resolvers/zod";
import { Save, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { EnumSelect } from "@/components/shared/enum-select";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PTO_ENTRY_STATUS_OPTIONS, PTO_ENTRY_TYPE_OPTIONS } from "@/lib/enums";
import { createPtoEntrySchema, type PtoEntryFormValues } from "@/schemas/pto-entry";

interface PtoEntryFormResult {
  startDate: string;
  endDate: string;
  totalHours: number | null;
  type: string;
  status: string;
  note: string | null;
}

interface PtoEntryFormDefaults {
  startDate?: string;
  endDate?: string;
  totalHours?: number | null;
  type?: string;
  status?: string;
  note?: string | null;
}

interface PtoEntryFormProps {
  defaultValues?: PtoEntryFormDefaults;
  onSubmit: (result: PtoEntryFormResult) => void;
  onCancel: () => void;
  isSaving: boolean;
  submitLabel?: string;
}

const EASTERN_TODAY_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/New_York",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Today's date in America/New_York, as "YYYY-MM-DD" (the "en-CA" locale
 * formats that way by default). Runs in the browser, so this business's
 * canonical Eastern "today" is used regardless of the visiting employee's
 * own device timezone — NOT `new Date().toISOString()`, which is always UTC
 * and rolls over to tomorrow's date starting at 8pm EDT / 7pm EST.
 */
function todayIso(): string {
  return EASTERN_TODAY_FORMATTER.format(new Date());
}

/**
 * One reusable form drives BOTH create and edit — the create dialog and the
 * edit dialog render this same component. See .claude/rules/frontend.md.
 */
export function PtoEntryForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSaving,
  submitLabel = "Save",
}: PtoEntryFormProps) {
  const form = useForm<PtoEntryFormValues>({
    resolver: zodResolver(createPtoEntrySchema),
    defaultValues: {
      startDate: defaultValues?.startDate ?? todayIso(),
      endDate: defaultValues?.endDate ?? todayIso(),
      totalHours: defaultValues?.totalHours ?? null,
      type: (defaultValues?.type as PtoEntryFormValues["type"]) ?? "vacation",
      status: (defaultValues?.status as PtoEntryFormValues["status"]) ?? "planned",
      note: defaultValues?.note ?? "",
    },
  });

  function handleSubmit(values: PtoEntryFormValues) {
    onSubmit({
      startDate: values.startDate,
      endDate: values.endDate,
      totalHours: values.totalHours ?? null,
      type: values.type,
      status: values.status,
      note: values.note?.trim() ? values.note : null,
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Start date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>End date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Type</FormLabel>
                <FormControl>
                  <EnumSelect
                    value={field.value}
                    onValueChange={field.onChange}
                    options={PTO_ENTRY_TYPE_OPTIONS}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <FormControl>
                  <EnumSelect
                    value={field.value}
                    onValueChange={field.onChange}
                    options={PTO_ENTRY_STATUS_OPTIONS}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="totalHours"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Hours (optional — leave blank for a full day)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="24"
                  placeholder="Full day"
                  name={field.name}
                  ref={field.ref}
                  onBlur={field.onBlur}
                  disabled={field.disabled}
                  value={field.value == null ? "" : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === "" ? null : e.target.valueAsNumber)
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Note</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Add a note..."
                  className="min-h-[80px]"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isSaving}>
            <Save className="h-4 w-4 mr-1" />
            {isSaving ? "Saving…" : submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
